import type { Building } from "building-types/building/buildingTypes";
import type { FoundationAssemblyOverrides } from "building-types/foundations/foundationAssemblyEntityTypes";
import type { HalfEdgeToFoundationPath } from "building-types/foundations/foundationTypes";
import type { LevelHalfEdgeAssociationTable } from "building-types/planarGraphs/planarGraphAssociationTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { RWTable, Table } from "common/data/table";
import { finalizeFoundationComponents } from "doc/elements/foundation/foundationComponentsTools";
import { foundationSystemSettingsFromSettingsConfig } from "doc/elements/foundation/foundationSettingsTools";
import { evalFormulas, resolveOptionsInFormula } from "doc/formula/evalFormulas";
import {
  collectIsolatedFoundationPointsFromColumns,
  collectIsolatedFoundationPointsFromStairSystems,
  getFoundationLevelIds,
  makeAutogeneratedIsolatedFoundationPatterns,
} from "../elements/foundation/autoFoundationSystem";
import { makeFoundationPaths } from "../elements/foundation/foundationPaths";
import { groupByWorldKey, worldKeyForBuildingEntity } from "../elements/level/levelTools";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

function select({
  settings,
  doc: {
    optionSets,
    instanceOptionSets,
    foundationComponents,
    foundationAssemblies,
    rooms: staticRooms,
    framingComponents,
    slabComponents,
    sheathingComponents,
    componentInstances,
  },
  optionChoices,
  levels,
  wainscoting,
  planarGraphs,
  planarGraphLevelAssociations,
  foundationPatterns,
  foundationAssemblyOverrides,
  rooms,
  stairSystems,
  columns,
  foundationPatternEdgeBehaviors,
  interiorPalette,
  exteriorPalette,
  floorPaths,
  datumAbstractions,
  enabledFeatures,
  idToParents,
}: Building) {
  const definitions = { optionSets, instanceOptionSets };
  return {
    optionChoices: resolveOptionsInFormula({ foundationAssemblyOverrides }, definitions, optionChoices),
    levels,
    wainscoting,
    planarGraphs,
    planarGraphLevelAssociations,
    foundationPatterns,
    docRooms: staticRooms,
    rooms,
    framingComponents,
    slabComponents,
    sheathingComponents,
    foundationSettings: settings.foundation,
    foundationComponents,
    foundationAssemblies,
    datumAbstractions,
    foundationAssemblyOverrides,
    doorSettings: settings.door,
    wallSettings: settings.wall,
    porchSettings: settings.porch,
    rearPorchSettings: settings.rearPorch ?? settings.porch,
    columnSettings: settings.column,
    interiorPalette,
    exteriorPalette,
    stairSystems,
    columns,
    foundationPatternEdgeBehaviors,
    floorPaths,
    horizontalAssemblies: enabledFeatures.horizontalAssemblies,
    idToParents,
    componentInstances,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  foundationSettings: "shallow",
  optionChoices: "shallow",
  foundationPatternEdgeBehaviors: "shallow",
  horizontalAssemblies: "shallow",
  idToParents: "shallow",
  componentInstances: "shallow",
});

function generateFoundationPathsForWorlds(input: ReturnType<typeof select>): {
  foundationPaths: Table<HalfEdgeToFoundationPath>;
  issues: BuildingGenerationIssue[];
} {
  const { levels, columns, stairSystems, wainscoting, idToParents, componentInstances } = input;
  const levelsByWorldKey = groupByWorldKey(levels, (id) =>
    worldKeyForBuildingEntity(id, idToParents, componentInstances),
  );
  const columnsByWorldKey = groupByWorldKey(columns, (_, column) =>
    worldKeyForBuildingEntity(column.levelId, idToParents, componentInstances),
  );
  const stairSystemsByWorldKey = groupByWorldKey(stairSystems, (_, stairSystem) =>
    worldKeyForBuildingEntity(stairSystem.levelId, idToParents, componentInstances),
  );
  const wainscotingByWorldKey = groupByWorldKey(wainscoting, (_, wainscot) =>
    worldKeyForBuildingEntity(wainscot.levelId, idToParents, componentInstances),
  );

  let allFoundationPaths: Table<HalfEdgeToFoundationPath> = {};
  let allIssues: BuildingGenerationIssue[] = [];
  for (const worldKey in levelsByWorldKey) {
    const { foundationPaths, issues } = generate({
      ...input,
      levels: levelsByWorldKey[worldKey] ?? {},
      columns: columnsByWorldKey[worldKey] ?? {},
      stairSystems: stairSystemsByWorldKey[worldKey] ?? {},
      wainscoting: wainscotingByWorldKey[worldKey] ?? {},
    });
    allFoundationPaths = { ...allFoundationPaths, ...foundationPaths };
    allIssues = [...allIssues, ...issues];
  }
  return { foundationPaths: allFoundationPaths, issues: allIssues };
}

function generate(input: ReturnType<typeof select>): {
  foundationPaths: Table<HalfEdgeToFoundationPath>;
  issues: BuildingGenerationIssue[];
} {
  if (Object.values(input.levels).length === 0) {
    return { foundationPaths: {}, issues: [] };
  }

  const finalizedFoundationComponents = finalizeFoundationComponents(
    input.foundationComponents,
    foundationSystemSettingsFromSettingsConfig(input.foundationSettings),
  );

  const { optionChoices } = input;
  const evaluatedFoundationAssemblyOverrides: LevelHalfEdgeAssociationTable<FoundationAssemblyOverrides> = evalFormulas(
    input.foundationAssemblyOverrides,
    (v) => optionChoices[v],
  );

  const issues: BuildingGenerationIssue[] = [];
  const foundationPaths: RWTable<HalfEdgeToFoundationPath> = {};
  const foundationLevelIds = getFoundationLevelIds(input.levels);

  const isolatedFoundationPointsFromStairSystems = collectIsolatedFoundationPointsFromStairSystems(
    input.levels,
    input.stairSystems,
    input.foundationPatternEdgeBehaviors,
  );
  const isolatedFoundationPointsFromColumns = collectIsolatedFoundationPointsFromColumns(
    input.columns,
    foundationLevelIds,
    input.rooms,
    input.levels,
  );

  const generatedIsolatedFoundationPatterns = makeAutogeneratedIsolatedFoundationPatterns(
    foundationLevelIds,
    input.planarGraphs,
    [...isolatedFoundationPointsFromStairSystems, ...isolatedFoundationPointsFromColumns],
  );

  for (const levelId of foundationLevelIds) {
    const levelGraph = input.planarGraphs[levelId];
    const level = input.levels[levelId];
    const levelFloorPaths = input.floorPaths[levelId];

    if (!levelFloorPaths) {
      throw new Error(`Floor paths not found for foundation level ${levelId}`);
    }

    const levelFoundationPaths = makeFoundationPaths({
      level,
      levels: input.levels,
      planarGraph: levelGraph,
      planarGraphs: input.planarGraphs,
      docRooms: input.docRooms,
      planarGraphLevelAssociations: input.planarGraphLevelAssociations,
      floorPaths: input.floorPaths,
      framingComponents: input.framingComponents,
      slabComponents: input.slabComponents,
      sheathingComponents: input.sheathingComponents,
      datumAbstractions: input.datumAbstractions,
      foundationPatterns: input.foundationPatterns,
      generatedIsolatedFoundationPatterns,
      wainscoting: input.wainscoting,
      doorSettings: input.doorSettings,
      foundationSettings: foundationSystemSettingsFromSettingsConfig(input.foundationSettings),
      foundationComponents: finalizedFoundationComponents,
      foundationAssemblies: input.foundationAssemblies,
      foundationAssemblyOverrides: evaluatedFoundationAssemblyOverrides,
      issues,
      horizontalAssemblies: input.horizontalAssemblies,
    });

    foundationPaths[levelId] = levelFoundationPaths;
  }

  return { foundationPaths, issues };
}

export default memo(select, generateFoundationPathsForWorlds, compare);
